import React, { useEffect, useState, ReactNode } from 'react'
import { IMinerNode } from '@/types/miner.interface'
import { useTranslations } from 'next-intl'
import getHashrateValue from '@/utils/getHashrateValue'

import {
  Button,
  Flex,
  Stack,
  Text,
  VStack,
  Box,
  Icon,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { FirmwareInfoModal } from './FirmwareInfoModal'
import { formatPrice } from '@/utils/formatPrice'
import { format, parseISO } from 'date-fns'
import { useAddToCart } from '@/hooks/useAddToCart'
import { isCurrentDateHigher } from '@/utils/date'
import { RelocationTooltip } from './RelocationTooltip'
import { isProductInCart } from '@/utils/isProductInCart'
import { getDaysLeft } from '@/utils/getDaysLeft'
import { useCart } from '@/hooks/useCart'
import { CheckIcon, InfoOutlineIcon } from '@chakra-ui/icons'

type Props = {
  items: IMinerNode[]
  paymentPlanButton?: ReactNode
  isSalePage?: boolean
}

const HardwareDetailCard = function ({
  items,
  paymentPlanButton = null,
  isSalePage = false,
}: Props) {
  const t = useTranslations()
  const { cart } = useCart()
  const [machineId, setMachineId] = useState<string>('')
  const alpha700 = useColorModeValue('blackAlpha.700', 'whiteAlpha.700')
  const {
    isOpen: isFirmwareModalOpen,
    onOpen: onFirmwareModalOpen,
    onClose: onFirmwareModalClose,
  } = useDisclosure()
  const bg = useColorModeValue('white', 'whiteAlpha.200')

  const getAvailableStock = (stock: number) => {
    if (stock > 10) {
      return '10+'
    }
    return stock
  }

  const getWarrantyDate = (date: string) => {
    if (date) {
      return isCurrentDateHigher(date)
        ? t('dashboard.machine-sale.details.expired')
        : format(parseISO(date), 'MM/dd/yyyy')
    } else {
      return '-'
    }
  }

  const isNonhostedProduct = (item: IMinerNode) => {
    if (item.productType === 'NON_HOSTED') {
      return true
    }
    return false
  }

  const [alterCartMutation, { loading: alterCartLoading }] = useAddToCart({
    productId: machineId,
  })

  useEffect(() => {
    if (machineId) {
      alterCartMutation()
      setMachineId('')
    }
  }, [machineId])

  return (
    <>
      <FirmwareInfoModal
        isOpen={isFirmwareModalOpen}
        onClose={onFirmwareModalClose}
      />
      {items &&
        items.length > 0 &&
        items.map((hardwareItem: IMinerNode, index: number) => {
          const productInCart = isProductInCart(hardwareItem.productId, cart)
          return (
            <VStack
              key={index}
              flexDirection={'column'}
              w={'full'}
              bg={bg}
              borderWidth={1}
              borderRadius="8px"
              borderColor={bg}
              boxShadow="0 3px 4px 0 rgba(0, 0, 0, 0.10)"
              px="18px"
              py="32px"
              mb="18px"
              spacing="12px"
            >
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                alignItems={'center'}
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('facilities-list.facility')}
                </Text>
                <Box w="50%" textAlign="left">
                  <Text fontSize={'12px'}>
                    {`${hardwareItem?.facilityDisplayName}, ${hardwareItem?.countryName}` ||
                      'N/A'}
                  </Text>
                  <RelocationTooltip facilityId={hardwareItem.facilityId} />
                </Box>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('hardware-list.labels.hosting-fee')}
                </Text>
                <Box w="50%" textAlign="left">
                  {!isNonhostedProduct(hardwareItem) ? (
                    <Text>
                      {formatPrice(
                        hardwareItem?.overriddenMonthlyHostingCharges ||
                          hardwareItem?.monthlyHostingCharges,
                        true
                      )}
                    </Text>
                  ) : (
                    <Text
                      textAlign="center"
                      fontWeight={'normal'}
                      whiteSpace={'nowrap'}
                      color="gray.500"
                    >
                      {t('common.labels.not-applicable')}
                    </Text>
                  )}
                </Box>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('hardware-list.estimated-online-date')}
                </Text>
                {isNonhostedProduct(hardwareItem) ? (
                  <Text w="50%" textAlign="left">
                    {hardwareItem?.nonhostedShippingTimeDescription || ''}
                  </Text>
                ) : (
                  <Text w="50%" textAlign="left">
                    {hardwareItem?.hostedTargetOnlineDate
                      ? format(
                          parseISO(hardwareItem.hostedTargetOnlineDate),
                          'MM/dd/yyyy'
                        )
                      : '-'}
                  </Text>
                )}
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('common.labels.condition')}
                </Text>
                <Text w="50%" textAlign="left" textTransform={'capitalize'}>
                  {hardwareItem?.productCondition?.replace('_', ' ')}
                </Text>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
                display={isSalePage ? 'none' : 'flex'}
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('common.labels.hashrate')}
                </Text>
                <Text w="50%" textAlign="left">
                  {getHashrateValue(
                    hardwareItem.minerModelAlgorithm,
                    Number(hardwareItem.minerModelHashratePerTerahash),
                    false
                  )}
                </Text>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
                display={isSalePage ? 'none' : 'flex'}
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('common.labels.power')}
                </Text>
                <Text
                  w="50%"
                  textAlign="left"
                >{`${hardwareItem.minerModelPowerConsumptionWatts.toLocaleString()} Watts`}</Text>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('hardware-list.labels.available-quantity')}
                </Text>
                <Text w="50%" textAlign="left">
                  {`${getAvailableStock(
                    hardwareItem.productAvailableQuantity
                  )}`}
                </Text>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justifyItems={'space-between'}
                align="center"
              >
                <Text fontWeight={600} w="50%" color={alpha700}>{`$/${t(
                  'common.labels.hashrate'
                )}`}</Text>
                <Flex w="50%" justify={'left'} alignItems={'center'}>
                  <Text>
                    {getHashrateValue(
                      hardwareItem?.minerModelAlgorithm,
                      Number(hardwareItem?.productPricePerHashrate),
                      true
                    )}
                  </Text>
                </Flex>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justify={'space-between'}
                align="center"
                display={isSalePage ? 'none' : 'flex'}
              >
                <Flex align="center" gap={1} w="50%">
                  <Text fontWeight="600" color={alpha700}>
                    {t('common.labels.firmware')}
                  </Text>
                  <Icon
                    as={InfoOutlineIcon}
                    boxSize={3}
                    cursor="pointer"
                    onClick={onFirmwareModalOpen}
                  />
                </Flex>
                <Text w="50%" textAlign="left">
                  {hardwareItem?.minerFirmware ??
                    hardwareItem?.manufacturerName}
                </Text>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justifyItems={'space-between'}
                align="center"
                display={isSalePage ? 'none' : 'flex'}
              >
                <Text fontWeight={600} w="50%" color={alpha700}>{`${t(
                  'common.labels.warranty-exp'
                )}`}</Text>
                <Flex w="50%" justify={'left'} alignItems={'center'}>
                  <Text>
                    {getWarrantyDate(
                      hardwareItem?.minerModelManufacturerWarrantyExp
                    )}
                  </Text>
                </Flex>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justifyItems={'space-between'}
                align="center"
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('common.labels.price')}
                </Text>
                <Flex w="50%" justify={'left'} alignItems={'center'}>
                  <Text>
                    {formatPrice(hardwareItem.productListingPrice, false)}
                  </Text>
                </Flex>
              </Flex>
              <Flex
                fontSize={'xs'}
                w="full"
                justifyItems={'space-between'}
                align="center"
                display={isSalePage ? 'flex' : 'none'}
              >
                <Text fontWeight="600" color={alpha700} w="50%">
                  {t('common.labels.days-listed')}
                </Text>
                <Flex w="50%" justify={'left'} alignItems={'center'}>
                  <Text>{getDaysLeft(hardwareItem.productDateListed)}</Text>
                </Flex>
              </Flex>
              <Stack
                w={{ base: 'full', md: '7/10' }}
                justifyItems={'center'}
                m="auto"
              >
                <Button
                  variant="darkBrand"
                  onClick={() => setMachineId(hardwareItem.productId)}
                  isLoading={
                    alterCartLoading && machineId === hardwareItem.productId
                  }
                  isDisabled={productInCart}
                  display={isSalePage ? 'none' : 'initial'}
                  className="gtm-add-to-cart"
                >
                  {productInCart ? (
                    <Box
                      color="black"
                      display="flex"
                      alignItems="center"
                      gap="2"
                    >
                      <CheckIcon />
                      {t('buttons.added')}
                    </Box>
                  ) : (
                    t('buttons.add-cart')
                  )}
                </Button>
                {hardwareItem.financingEligible && paymentPlanButton}
              </Stack>
            </VStack>
          )
        })}
    </>
  )
}

export default HardwareDetailCard
